import { useRef, useEffect } from "react";

// Typable DD / MM / YYYY input with auto-advance and Backspace nav.
// value: { d: string, m: string, y: string }
export const DateInput = ({ value, onChange, autoFocus = false }) => {
  const dRef = useRef(null);
  const mRef = useRef(null);
  const yRef = useRef(null);

  useEffect(() => {
    if (autoFocus && dRef.current) dRef.current.focus();
  }, [autoFocus]);

  const setField = (key, v) => {
    const digits = v.replace(/\D/g, "");
    onChange({ ...value, [key]: digits });
  };

  const handleChange = (key, ref, maxLen, nextRef) => (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, maxLen);
    setField(key, digits);
    if (digits.length === maxLen && nextRef?.current) {
      nextRef.current.focus();
    }
  };

  const handleKeyDown = (key, prevRef) => (e) => {
    if (e.key === "Backspace" && !value[key] && prevRef?.current) {
      prevRef.current.focus();
    }
  };

  const baseCls =
    "bg-black/30 border border-white/10 text-white text-center text-2xl sm:text-3xl font-display tracking-wider rounded-lg h-16 sm:h-20 focus:outline-none focus:border-stardust focus:ring-1 focus:ring-stardust placeholder:text-white/15 transition-colors";

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3" data-testid="form-dob-typed">
      <div className="flex flex-col items-center w-full">
        <input
          ref={dRef}
          data-testid="form-dob-day"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="DD"
          maxLength={2}
          value={value.d}
          onChange={handleChange("d", dRef, 2, mRef)}
          className={`${baseCls} w-full`}
          aria-label="Day"
        />
        <span className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mt-2">Day</span>
      </div>
      <span className="text-stardust/40 text-2xl sm:text-3xl pb-7">/</span>
      <div className="flex flex-col items-center w-full">
        <input
          ref={mRef}
          data-testid="form-dob-month"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="MM"
          maxLength={2}
          value={value.m}
          onChange={handleChange("m", mRef, 2, yRef)}
          onKeyDown={handleKeyDown("m", dRef)}
          className={`${baseCls} w-full`}
          aria-label="Month"
        />
        <span className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mt-2">Month</span>
      </div>
      <span className="text-stardust/40 text-2xl sm:text-3xl pb-7">/</span>
      <div className="flex flex-col items-center w-full">
        <input
          ref={yRef}
          data-testid="form-dob-year"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="YYYY"
          maxLength={4}
          value={value.y}
          onChange={handleChange("y", yRef, 4, null)}
          onKeyDown={handleKeyDown("y", mRef)}
          className={`${baseCls} w-[140%]`}
          aria-label="Year"
        />
        <span className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mt-2">Year</span>
      </div>
    </div>
  );
};

export const isValidDate = ({ d, m, y }) => {
  const day = parseInt(d, 10);
  const month = parseInt(m, 10);
  const year = parseInt(y, 10);
  if (!day || !month || !year) return false;
  if (year < 1900 || year > new Date().getFullYear()) return false;
  if (month < 1 || month > 12) return false;
  const dt = new Date(year, month - 1, day);
  return (
    dt.getFullYear() === year &&
    dt.getMonth() === month - 1 &&
    dt.getDate() === day
  );
};

export const formatISO = ({ d, m, y }) =>
  `${y.padStart(4, "0")}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
